import {
  ReportingTeamUser,
  Stats,
  StatsCoachingNote,
  StatsContactReview,
  TeamProfile,
} from '../../Types';
import {
  CoachingsTableSortColumn,
  CoachingTableItemStatus,
  ContactReviewsTableSortColumn,
  MonthSelection,
  TmTableSortColumn,
} from './types';
import { DeliveredStatus } from '@amzn/aet-coachingform-kit';
import { convertEnumToObject } from '../../Util';
import { getCalendarMonth } from '@amzn/aet-coachingstats-kit';
import _ from 'lodash';
import { UserInfo } from '../../api/Naws';
import moment from 'moment';
import {
  getAmazonWeeksOfCurrentMonth,
  getAmazonWeeksOfLastMonth,
} from '@amzn/aet-coachingstats-kit';
import { getUserIdWithPrefix } from '../../Util/Helper';
import {
  BasicStatsForOM,
  TMCoachingStatsSummaryForOM,
} from '../../Types/CoachingStats';
import { SortDirection } from '../../Types/SortDirection';

export const isCoachingCompleted = (coachingsForOM: BasicStatsForOM) => {
  return (
    coachingsForOM.totalCoachingRequired <= coachingsForOM.completedCoachings
  );
};

export const getCoachingNoteStatus = (
  statsCoachingNote: StatsCoachingNote
): CoachingTableItemStatus => {
  const DeliveredStatusObject = convertEnumToObject(DeliveredStatus);
  const WARNING_TAG_TYPE = 'warning';
  const SUCCESS_TAG_TYPE = 'success';
  const THEME_TAG_TYPE = 'theme';
  const NEUTRAL_TAG_TYPE = 'neutral';
  const DRAFT_TAG_TEXT = 'Draft';
  const DELIVERED_TAG_TEXT = 'Delivered';
  const REVIEWED_TAG_TEXT = 'Reviewed';
  const NO_SESSION_TEXT = 'No Session';
  const DRAFT_ID = 'cs_perfportal_coaching_page_tm_table_tag_draft';
  const DELIVERED_ID = 'cs_perfportal_coaching_page_tm_table_tag_delivered';
  const REVIEWED_ID = 'cs_perfportal_coaching_page_tm_table_tag_reviewed';
  const NO_SESSION_ID = 'cs_perfportal_coaching_page_tm_table_tag_no_session';

  switch (statsCoachingNote.isDelivered) {
    case DeliveredStatusObject.DRAFT:
      return {
        tagType: WARNING_TAG_TYPE,
        tagText: DRAFT_TAG_TEXT,
        id: DRAFT_ID,
      };
    case DeliveredStatusObject.OPEN_DELIVERED:
      return {
        tagType: SUCCESS_TAG_TYPE,
        tagText: DELIVERED_TAG_TEXT,
        id: DELIVERED_ID,
      };
    case DeliveredStatusObject.DELIVERED:
      return {
        tagType: SUCCESS_TAG_TYPE,
        tagText: DELIVERED_TAG_TEXT,
        id: DELIVERED_ID,
      };
    case DeliveredStatusObject.OPEN_REVIEWED:
      return {
        tagType: THEME_TAG_TYPE,
        tagText: REVIEWED_TAG_TEXT,
        id: REVIEWED_ID,
      };
    case DeliveredStatusObject.CLOSED:
      return {
        tagType: THEME_TAG_TYPE,
        tagText: REVIEWED_TAG_TEXT,
        id: REVIEWED_ID,
      };
    default:
      return {
        tagType: NEUTRAL_TAG_TYPE,
        tagText: NO_SESSION_TEXT,
        id: NO_SESSION_ID,
      };
  }
};

export const getAlias = (
  directReports: ReportingTeamUser[],
  selectedTm: string,
  cscEntityPrefix: string
) => {
  return directReports.find(
    directReport =>
      selectedTm ===
      getUserIdWithPrefix(cscEntityPrefix, directReport.relatedUser.userID)
  )!.relatedUser.alias;
};

export const getFullName = (
  directReports: ReportingTeamUser[],
  selectedTm: string,
  cscEntityPrefix: string
) => {
  return directReports.find(
    directReport =>
      selectedTm ===
      getUserIdWithPrefix(cscEntityPrefix, directReport.relatedUser.userID)
  )!.relatedUser.displayName;
};

export const getDirectReportCount = (
  directReports: ReportingTeamUser[],
  selectedTm: string,
  cscEntityPrefix: string
) => {
  return directReports.find(
    directReport =>
      selectedTm ===
      getUserIdWithPrefix(cscEntityPrefix, directReport.relatedUser.userID)
  )!.subReports!.length;
};

export const getUserFromIdWithPrefix = (userIdWithPrefix: string) => {
  return userIdWithPrefix.split(':')[1];
};

export const buildUserInfoFromDirectReportAlias = (
  userAlias: string
): UserInfo => {
  return {
    userName: userAlias,
  };
};

export const buildCoachingsTableProps = (
  coachingsAndContactReviews: Stats | undefined,
  monthSelection: MonthSelection
) => {
  if (coachingsAndContactReviews) {
    const coachingNotes = coachingsAndContactReviews.coachingNotes;
    return coachingNotes.filter(
      coachingNote =>
        coachingNote.createdAt &&
        isDateInMonth(coachingNote.createdAt, monthSelection)
    );
  } else {
    return [];
  }
};

export const buildContactReviewsTableProps = (
  coachingsAndContactReviews: Stats | undefined,
  monthSelection: MonthSelection
) => {
  if (coachingsAndContactReviews) {
    const contactReviews = coachingsAndContactReviews.contactReviews;
    return contactReviews.filter(
      contactReview =>
        contactReview.deliveredAt &&
        isDateInMonth(contactReview.deliveredAt, monthSelection)
    );
  } else {
    return [];
  }
};

export const getReviewedContactReviewsCountForMonth = (
  coachingsAndContactReviews: Stats | undefined,
  monthSelection: MonthSelection
) => {
  if (coachingsAndContactReviews) {
    return coachingsAndContactReviews.contactReviews.filter(
      contactReview =>
        contactReview.deliveredAt &&
        isDateInMonth(contactReview.deliveredAt, monthSelection)
    ).length;
  } else {
    return 0;
  }
};

export const isDateInMonth = (date: string, monthSelection: MonthSelection) => {
  const selectedMonth =
    monthSelection === MonthSelection.THIS_MONTH
      ? moment().month
      : moment().subtract(1, 'month').month;

  return moment(date).month === selectedMonth;
};

export const getStartAndEndWeekParams = (monthSelection: MonthSelection) => {
  const weeksOfCurrentMonth = getAmazonWeeksOfCurrentMonth();
  const weeksOfLastMonth = getAmazonWeeksOfLastMonth();
  return `startweek=${
    monthSelection === MonthSelection.THIS_MONTH
      ? weeksOfCurrentMonth[0].year
      : weeksOfLastMonth[0].year
  }-${
    monthSelection === MonthSelection.THIS_MONTH
      ? weeksOfCurrentMonth[0].week
      : weeksOfLastMonth[0].week
  }&endweek=${
    monthSelection === MonthSelection.THIS_MONTH
      ? weeksOfCurrentMonth[weeksOfCurrentMonth.length - 1].year
      : weeksOfLastMonth[weeksOfLastMonth.length - 1].year
  }-${
    monthSelection === MonthSelection.THIS_MONTH
      ? weeksOfCurrentMonth[weeksOfCurrentMonth.length - 1].week
      : weeksOfLastMonth[weeksOfLastMonth.length - 1].week
  }`;
};

export const getTMDirectReportsOfOM = (
  teamProfile: TeamProfile
): ReportingTeamUser[] => {
  return teamProfile.reportingTeam.filter(user => user.isManager === true);
};

export const getTMDirectReportsOfOMMap = (
  tmDirectReportsOfOM: ReportingTeamUser[],
  cscEntityPrefix: string
): Record<string, ReportingTeamUser> => {
  return _.chain(tmDirectReportsOfOM)
    .keyBy(item =>
      getUserIdWithPrefix(cscEntityPrefix, item.relatedUser.userID)
    )
    .value();
};

export const isLastMonthDataAvailable = (
  monthSelection: MonthSelection
): boolean => {
  /* TODO: Remove this method after May 2025. */
  const now = moment().toISOString();
  return (
    monthSelection === MonthSelection.LAST_MONTH &&
    getCalendarMonth(now).month === 5 &&
    getCalendarMonth(now).year === 2025
  );
};

export const coachingsTableCustomSort = (
  a: StatsCoachingNote,
  b: StatsCoachingNote,
  sortColumn: CoachingsTableSortColumn,
  sortDirection: SortDirection
): number => {
  if (
    getCoachingNoteFieldForSortColumn(a, sortColumn) <
    getCoachingNoteFieldForSortColumn(b, sortColumn)
  ) {
    return sortDirection === SortDirection.ASCENDING ? -1 : 1;
  }
  if (
    getCoachingNoteFieldForSortColumn(a, sortColumn) >
    getCoachingNoteFieldForSortColumn(b, sortColumn)
  ) {
    return sortDirection === SortDirection.ASCENDING ? 1 : -1;
  }
  return 0;
};

export const getCoachingNoteFieldForSortColumn = (
  coachingNote: StatsCoachingNote,
  sortColumn: CoachingsTableSortColumn
) => {
  switch (sortColumn) {
    case CoachingsTableSortColumn.DATE:
      return coachingNote.deliveredAt;
    case CoachingsTableSortColumn.ASSOCIATE_NAME:
      return coachingNote.recipient.userName;
    case CoachingsTableSortColumn.COACHING_STATUS:
      return getCoachingNoteStatus(coachingNote).tagType;
    default:
      return coachingNote.deliveredAt;
  }
};

export const contactReviewsTableCustomSort = (
  a: StatsContactReview,
  b: StatsContactReview,
  sortColumn: ContactReviewsTableSortColumn,
  sortDirection: SortDirection
): number => {
  if (
    getContactReviewsFieldForSortColumn(a, sortColumn) <
    getContactReviewsFieldForSortColumn(b, sortColumn)
  ) {
    return sortDirection === SortDirection.ASCENDING ? -1 : 1;
  }
  if (
    getContactReviewsFieldForSortColumn(a, sortColumn) >
    getContactReviewsFieldForSortColumn(b, sortColumn)
  ) {
    return sortDirection === SortDirection.ASCENDING ? 1 : -1;
  }
  return 0;
};

export const getContactReviewsFieldForSortColumn = (
  contactReview: StatsContactReview,
  sortColumn: ContactReviewsTableSortColumn
) => {
  switch (sortColumn) {
    case ContactReviewsTableSortColumn.DATE:
      return contactReview.deliveredAt;
    case ContactReviewsTableSortColumn.ASSOCIATE_NAME:
      return contactReview.agentUserName;
    default:
      return contactReview.deliveredAt;
  }
};

export const tmTableCustomSort = (
  a: TMCoachingStatsSummaryForOM,
  b: TMCoachingStatsSummaryForOM,
  sortColumn: TmTableSortColumn,
  sortDirection: SortDirection
): number => {
  if (
    getTmTableFieldForSortColumn(a, sortColumn) <
    getTmTableFieldForSortColumn(b, sortColumn)
  ) {
    return sortDirection === SortDirection.ASCENDING ? -1 : 1;
  }
  if (
    getTmTableFieldForSortColumn(a, sortColumn) >
    getTmTableFieldForSortColumn(b, sortColumn)
  ) {
    return sortDirection === SortDirection.ASCENDING ? 1 : -1;
  }
  return 0;
};

export const getTmTableFieldForSortColumn = (
  tmCoachingStatsSummary: TMCoachingStatsSummaryForOM,
  sortColumn: TmTableSortColumn
) => {
  switch (sortColumn) {
    case TmTableSortColumn.ASSOCIATE_NAME:
      return tmCoachingStatsSummary.tmInfo.fullName;
    case TmTableSortColumn.STATUS:
      return (
        isCoachingCompleted(
          tmCoachingStatsSummary.statsInfo.monthlyCoachingStatsSummary
        ) &&
        isCoachingCompleted(
          tmCoachingStatsSummary.statsInfo.monthlyCoachingStatsSummary
        )
      );
    case TmTableSortColumn.MONTHLY_COACHINGS_COMPLETED:
      return tmCoachingStatsSummary.statsInfo.monthlyCoachingStatsSummary
        .completedCoachings;
    case TmTableSortColumn.WEEKLY_COACHINGS_COMPLETED:
      return tmCoachingStatsSummary.statsInfo
        .aggregatedWeeklyCoachingStatsSummary.completedCoachings;
    default:
      return tmCoachingStatsSummary.tmInfo.fullName;
  }
};
